import type { ApiResponse } from '../../shared/types.js';

/**
 * - Dev: `/api` (Vite proxy).
 * - Prod: `VITE_API_BASE_URL` if set, else API host (SPA lives on another subdomain).
 */
const raw = import.meta.env.VITE_API_BASE_URL as string | undefined;
export const API_BASE_URL = (() => {
  if (typeof raw === 'string' && raw.trim() !== '') return raw.trim().replace(/\/$/, '');
  // Use MODE so the branch is a compile-time string compare (Vite inlines MODE).
  if (import.meta.env.MODE !== 'production') return '/api';
  return 'https://screener-config.ambition-performance.de/api';
})();

let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = [];

function processQueue(error: unknown): void {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(undefined);
  });
  failedQueue = [];
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  const config: RequestInit = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  let res = await fetch(url, config);

  if (res.status === 401 && !endpoint.includes('/auth/')) {
    if (isRefreshing) {
      await new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
      res = await fetch(url, config);
    } else {
      isRefreshing = true;
      const refreshed = await refreshAccessToken();
      isRefreshing = false;

      if (refreshed) {
        processQueue(null);
        res = await fetch(url, config);
      } else {
        processQueue(new Error('Refresh failed'));
        window.location.href = '/login';
        return { success: false, error: { code: 'SESSION_EXPIRED', message: 'Session expired' } };
      }
    }
  }

  const data: ApiResponse<T> = await res.json();
  return data;
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  // Body beim DELETE: „Jobtabelle leeren" (FR-7) verlangt { confirm: true }.
  // fetch erlaubt das, nur der schmale Wrapper hier tat es bisher nicht.
  delete: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),

  upload: async <T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> => {
    const url = `${API_BASE_URL}${endpoint}`;
    const res = await fetch(url, { method: 'POST', credentials: 'include', body: formData });
    return res.json();
  },
};
